import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const StatsModal = ({ isOpen, onClose, stats }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('stats');

  if (!isOpen) return null;

  // Calculate detailed stats
  const totalBets = (stats.bets4x || 0) + (stats.bets3x || 0) + (stats.bets2x || 0) + (stats.bets1x || 0) + (stats.folds || 0);
  
  const handTypeStats = [
    { name: 'Royal Flush', key: 'royalFlush', color: '#ffd700' },
    { name: 'Straight Flush', key: 'straightFlush', color: '#ff8c00' },
    { name: 'Four of a Kind', key: 'fourKind', color: '#a855f7' },
    { name: 'Full House', key: 'fullHouse', color: '#3b82f6' },
    { name: 'Flush', key: 'flush', color: '#22c55e' },
    { name: 'Straight', key: 'straight', color: '#14b8a6' },
    { name: 'Three of a Kind', key: 'threeKind', color: '#f59e0b' },
    { name: 'Two Pair', key: 'twoPair', color: '#6366f1' },
    { name: 'Pair', key: 'pair', color: '#8b5cf6' },
    { name: 'High Card', key: 'highCard', color: '#6b7280' },
  ];

  const betTypeStats = [
    { name: t('preflop4x') || 'Pre-flop 4X', key: 'bets4x', wins: 'wins4x', losses: 'losses4x', color: '#22c55e' },
    { name: t('preflop3x') || 'Pre-flop 3X', key: 'bets3x', wins: 'wins3x', losses: 'losses3x', color: '#3b82f6' },
    { name: t('flop2x') || 'Flop 2X', key: 'bets2x', wins: 'wins2x', losses: 'losses2x', color: '#f59e0b' },
    { name: t('river1x') || 'River 1X', key: 'bets1x', wins: 'wins1x', losses: 'losses1x', color: '#ef4444' },
    { name: 'Fold', key: 'folds', wins: 'goodFolds', losses: null, color: '#6b7280' },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden"
        style={{ 
          background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
          border: '2px solid rgba(255,215,0,0.3)',
          boxShadow: '0 0 50px rgba(255,215,0,0.2)'
        }}
      >
        {/* Header */}
        <div 
          className="flex justify-between items-center p-4"
          style={{ borderBottom: '1px solid rgba(255,215,0,0.2)' }}
        >
          <h2 className="text-xl font-bold" style={{ color: '#ffd700' }}>📊 {t('statistics')}</h2>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.1)' }}
          >
            <span style={{ color: '#ccc' }}>✕</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex p-2 gap-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
          {['stats', 'hands', 'bets'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 rounded-lg font-bold text-xs md:text-sm transition-all ${
                activeTab === tab ? '' : 'opacity-60'
              }`}
              style={{
                background: activeTab === tab ? 'linear-gradient(145deg, #ffd700, #b8860b)' : 'transparent',
                color: activeTab === tab ? '#1a1a2e' : '#ccc'
              }}
            >
              {tab === 'stats' ? t('overview') || 'OVERVIEW' : 
               tab === 'hands' ? t('byHand') || 'BY HAND' : 
               t('byBet') || 'BY BET'}
            </button>
          ))}
        </div>

        <div className="p-4 md:p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          
          {/* Overview Tab */}
          {activeTab === 'stats' && (
            <>
              {/* Hands Played */}
              <div className="text-center mb-6">
                <div className="text-xs tracking-wider mb-1" style={{ color: '#888' }}>{t('handsPlayed')}</div>
                <div className="text-5xl font-black" style={{ color: '#ffd700' }}>{stats.handsPlayed}</div>
              </div>

              {/* Win/Lose/Fold */}
              <div className="flex justify-around mb-6">
                <div className="text-center">
                  <div className="text-xs tracking-wider mb-1" style={{ color: '#22c55e' }}>{t('win')}</div>
                  <div className="text-2xl font-bold" style={{ color: '#22c55e' }}>{stats.winPercent}%</div>
                  <div className="text-xs" style={{ color: '#666' }}>({stats.wins || 0})</div>
                </div>
                <div className="text-center">
                  <div className="text-xs tracking-wider mb-1" style={{ color: '#ef4444' }}>{t('lose')}</div>
                  <div className="text-2xl font-bold" style={{ color: '#ef4444' }}>{stats.losePercent}%</div>
                  <div className="text-xs" style={{ color: '#666' }}>({stats.losses || 0})</div>
                </div>
                <div className="text-center">
                  <div className="text-xs tracking-wider mb-1" style={{ color: '#888' }}>Fold</div>
                  <div className="text-2xl font-bold" style={{ color: '#888' }}>{stats.foldPercent}%</div>
                  <div className="text-xs" style={{ color: '#666' }}>({stats.folds || 0})</div>
                </div>
              </div>

              {/* Good Folds */}
              {(stats.folds || 0) > 0 && (
                <div className="text-center mb-4 p-3 rounded-xl" style={{ background: 'rgba(59,130,246,0.1)' }}>
                  <div className="text-xs tracking-wider mb-1" style={{ color: '#3b82f6' }}>{t('goodFolds') || 'GOOD FOLDS'}</div>
                  <div className="text-xl font-bold" style={{ color: '#3b82f6' }}>
                    {stats.goodFolds || 0} / {stats.folds || 0} ({stats.folds > 0 ? Math.round(((stats.goodFolds || 0) / stats.folds) * 100) : 0}%)
                  </div>
                </div>
              )}

              {/* Winnings */}
              <div 
                className="text-center p-4 rounded-xl mb-4"
                style={{ background: 'rgba(0,0,0,0.3)' }}
              >
                <div className="text-xs tracking-wider mb-1" style={{ color: '#888' }}>{t('netWinnings')}</div>
                <div 
                  className="text-3xl font-black"
                  style={{ color: stats.winnings >= 0 ? '#22c55e' : '#ef4444' }}
                >
                  {stats.winnings >= 0 ? '+' : ''}${stats.winnings}
                </div>
              </div>

              {/* Trips Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(168,85,247,0.1)' }}>
                  <div className="text-xs tracking-wider mb-1" style={{ color: '#a855f7' }}>{t('tripsWins') || 'TRIPS WINS'}</div>
                  <div className="text-xl font-bold" style={{ color: '#a855f7' }}>{stats.tripsWins || 0}</div>
                  <div className="text-xs" style={{ color: '#666' }}>+${stats.tripsWinnings || 0}</div>
                </div>
                <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(59,130,246,0.1)' }}>
                  <div className="text-xs tracking-wider mb-1" style={{ color: '#3b82f6' }}>{t('blindBonuses') || 'BLIND BONUSES'}</div>
                  <div className="text-xl font-bold" style={{ color: '#3b82f6' }}>{stats.blindBonuses || 0}</div>
                  <div className="text-xs" style={{ color: '#666' }}>+${stats.blindBonusWinnings || 0}</div>
                </div>
              </div>
            </>
          )}

          {/* By Hand Tab */}
          {activeTab === 'hands' && (
            <div className="space-y-2">
              <div className="text-xs text-center mb-4" style={{ color: '#888' }}>
                {t('handTypeStats') || 'Wins & Losses by Hand Type'}
              </div>
              {handTypeStats.map((hand) => {
                const wins = stats[`${hand.key}Wins`] || 0;
                const losses = stats[`${hand.key}Losses`] || 0;
                const total = wins + losses;
                const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
                
                return (
                  <div 
                    key={hand.key}
                    className="flex items-center justify-between p-2 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: hand.color }}></div>
                      <span className="text-sm" style={{ color: '#ccc' }}>{hand.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs" style={{ color: '#22c55e' }}>W:{wins}</span>
                      <span className="text-xs" style={{ color: '#ef4444' }}>L:{losses}</span>
                      {total > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ 
                          background: winRate >= 50 ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)',
                          color: winRate >= 50 ? '#22c55e' : '#ef4444'
                        }}>
                          {winRate}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Total hands count */}
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,215,0,0.2)' }}>
                <div className="text-xs text-center" style={{ color: '#888' }}>
                  {t('bestHand') || 'Best Hand'}: <span style={{ color: '#ffd700' }}>{stats.bestHand || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* By Bet Tab */}
          {activeTab === 'bets' && (
            <div className="space-y-3">
              <div className="text-xs text-center mb-4" style={{ color: '#888' }}>
                {t('betTypeStats') || 'Performance by Bet Timing'}
              </div>
              
              {betTypeStats.map((bet) => {
                const count = stats[bet.key] || 0;
                const wins = stats[bet.wins] || 0;
                const losses = bet.losses ? (stats[bet.losses] || 0) : (count - wins);
                const winRate = count > 0 ? Math.round((wins / count) * 100) : 0;
                const barWidth = totalBets > 0 ? (count / totalBets) * 100 : 0;
                
                return (
                  <div key={bet.key} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold" style={{ color: bet.color }}>{bet.name}</span>
                      <span className="text-sm" style={{ color: '#ccc' }}>{count}x</span>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="h-2 rounded-full mb-2" style={{ background: 'rgba(0,0,0,0.3)' }}>
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ width: `${barWidth}%`, background: bet.color }}
                      ></div>
                    </div>
                    
                    <div className="flex justify-between text-xs">
                      <span style={{ color: '#22c55e' }}>{t('wins') || 'Wins'}: {wins}</span>
                      {bet.losses && <span style={{ color: '#ef4444' }}>{t('losses') || 'Losses'}: {losses}</span>}
                      {count > 0 && (
                        <span style={{ color: winRate >= 50 ? '#22c55e' : '#ef4444' }}>
                          {winRate}% {t('winRate') || 'win rate'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Optimal play advice */}
              <div className="mt-4 p-3 rounded-xl" style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.3)' }}>
                <div className="text-xs font-bold mb-1" style={{ color: '#ffd700' }}>💡 {t('tip') || 'Tip'}</div>
                <div className="text-xs" style={{ color: '#ccc' }}>
                  {t('optimalPlayTip') || 'Optimal play: Bet 4X with strong pairs and suited aces. Never fold before the river!'}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default StatsModal;
